import { MenuItem, TextFieldProps, useMediaQuery } from "@mui/material"
import { Field } from "formik"
import { TextField } from "formik-mui"
import styled from "styled-components"

const Styled = {
  Field: styled(Field)<{ $hiddenLabel?: boolean }>`
    .MuiSelect-root {
      &:focus-visible {
        border: 2px dashed white;
        outline: 3px dashed #0e3487;
        outline-offset: 2px;
      }
    }

    ${(p) => p?.$hiddenLabel && `label { display: none; }`}
    width: 100%;
    .MuiFormHelperText-contained {
      margin-right: 0;
    }
    .MuiFormHelperText-root {
      font-size: 12px;
    }
    /* text alignment: */
    .MuiSelect-outlined.MuiSelect-outlined {
      text-align: initial;
      flex-direction: row;
    }

    .MuiSelect-select {
      height: 1.1876em !important;
      min-height: unset !important;
      padding-left: 32px;
      padding-right: 10px;
    }
    .MuiSelect-icon {
      left: 7px;
      right: unset;
    }
  `,
}

// extends BaseTextFieldProps
type CommonSelectProps<T> = TextFieldProps & {
  name: string
  label?: string
  required?: boolean
  selectValues: T[]
  map(value: T): { id: string | number; name: string }
  placeholder?: string
}

function FormikSelect<T>({
  name,
  label,
  required = false,
  selectValues,
  map,
  placeholder,
  hiddenLabel,
  ...props
}: CommonSelectProps<T>) {
  const isMobile = useMediaQuery("(max-width:768px)")
  const { SelectProps, ...textFieldProps } = props

  const existingMenuProps = SelectProps?.MenuProps
  const mobilePaperSx = isMobile
    ? {
        width: "calc(100vw - 32px)",
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "min(50dvh, 320px)",
      }
    : undefined

  const mergedSelectProps = {
    ...SelectProps,
    MenuProps: {
      ...(existingMenuProps ?? {}),
      disableScrollLock: true,
      hideBackdrop: false,
      disableAutoFocus: true,
      disableEnforceFocus: true,
      disableRestoreFocus: true,
      transitionDuration: 0,
      autoFocus: false,
      disableAutoFocusItem: true,
      ...(isMobile
        ? {
            disablePortal: true,
            marginThreshold: 16,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "center",
            },
            slotProps: {
              ...(existingMenuProps?.slotProps ?? {}),
              backdrop: {
                ...(typeof existingMenuProps?.slotProps?.backdrop === "object"
                  ? existingMenuProps.slotProps.backdrop
                  : {}),
                sx: {
                  ...(typeof existingMenuProps?.slotProps?.backdrop === "object"
                    ? (
                        existingMenuProps.slotProps.backdrop as {
                          sx?: Record<string, unknown>
                        }
                      )?.sx
                    : undefined),
                  transition: isMobile
                    ? "none !important"
                    : typeof existingMenuProps?.slotProps?.backdrop === "object"
                    ? (
                        existingMenuProps.slotProps.backdrop as {
                          sx?: Record<string, unknown>
                        }
                      )?.sx?.transition
                    : undefined,
                },
              },
              paper: {
                ...(typeof existingMenuProps?.slotProps?.paper === "object"
                  ? existingMenuProps.slotProps.paper
                  : {}),
                sx: {
                  ...(typeof existingMenuProps?.slotProps?.paper === "object"
                    ? (
                        existingMenuProps.slotProps.paper as {
                          sx?: Record<string, unknown>
                        }
                      )?.sx
                    : undefined),
                  ...mobilePaperSx,
                  transition: isMobile
                    ? "none !important"
                    : typeof existingMenuProps?.slotProps?.paper === "object"
                    ? (
                        existingMenuProps.slotProps.paper as {
                          sx?: Record<string, unknown>
                        }
                      )?.sx?.transition
                    : undefined,
                  transformOrigin: "top !important",
                },
              },
              root: {
                ...(existingMenuProps?.slotProps?.root ?? {}),
                sx: {
                  ...(existingMenuProps?.slotProps?.root as { sx?: object })
                    ?.sx,
                  right: isMobile ? "unset !important" : undefined,
                  bottom: isMobile ? "unset !important" : undefined,
                },
              },
            },
          }
        : {}),
    },
  }

  return (
    <Styled.Field
      component={TextField}
      select
      name={name}
      id={name}
      required={required}
      label={label}
      helperText={" "}
      $hiddenLabel={hiddenLabel}
      placeholder={placeholder}
      SelectProps={mergedSelectProps}
      {...textFieldProps}
    >
      {selectValues?.map((value, i) => {
        const { name, id } = map(value)
        return (
          <MenuItem key={i} value={id}>
            {name}
          </MenuItem>
        )
      })}
    </Styled.Field>
  )
}

export default FormikSelect
